import React from 'react';
import Table from '../../components/common/Table';
import { MemberDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { getBadgeColors, renderBooleanBadge } from '../../utils/displayUtils';
import Badge from '../common/Badge';
import createMemberActions from '../actionMenus/MemberActions';
import FileIcons from '../common/FileIcons';
import MemberNoteIcons from '../common/MemberNoteIcons';

interface MemberTableProps {
  data: MemberDTO[];
  statusFromUrl: string;
  handleViewMemberDetails: (row: MemberDTO) => void;
  handleEdit: (row: MemberDTO) => void;
  handleDelete: (row: MemberDTO) => Promise<void>;
  handleSendEmail: (row: MemberDTO) => void;
  handleViewRecordEmails: (row: MemberDTO) => void;
  handleArchive: (row: MemberDTO) => Promise<void>;
  handleRestore: (row: MemberDTO) => Promise<void>;
  handleRestoreFromArchive: (row: MemberDTO) => Promise<void>;
  handleImpersonateMemberLogin: (row: MemberDTO) => void;
  handleChangeStatus: (row: MemberDTO) => void;
  handleAddNote: (row: MemberDTO) => void;
  handleViewNoteHistory: (row: MemberDTO) => void;
  handleViewInvoiceHistory: (row: MemberDTO) => void;
  handleSendProofOfPaymentLink: (row: MemberDTO) => void;
  handleSendPasswordResetLink: (row: MemberDTO) => void;
  handleCompleteEftPayment: (row: MemberDTO) => void;
  handleViewMandate: (row: MemberDTO) => void;
  handleEditMandate: (row: MemberDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
  selectedIds?: Set<string>;
  onSelectRow?: (id: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
}

const MemberTable: React.FC<MemberTableProps> = ({
  data,
  statusFromUrl,
  handleViewMemberDetails,
  handleEdit,
  handleDelete,
  handleSendEmail,
  handleViewRecordEmails,
  handleArchive,
  handleRestore,
  handleRestoreFromArchive,
  handleImpersonateMemberLogin,
  handleChangeStatus,
  handleAddNote,
  handleViewNoteHistory,
  handleViewInvoiceHistory,
  handleSendProofOfPaymentLink,
  handleSendPasswordResetLink,
  handleCompleteEftPayment,
  handleViewMandate,
  handleEditMandate,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
  selectedIds = new Set(),
  onSelectRow,
  onSelectAll,
}) => {
  const allCurrentPageIds = data.map(row => row.id).filter((id): id is string => !!id);
  const allSelected = allCurrentPageIds.length > 0 && allCurrentPageIds.every(id => selectedIds.has(id));
  const someSelected = allCurrentPageIds.some(id => selectedIds.has(id)) && !allSelected;

  return (
    <Table
      columns={[
        {
          label: (
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={(e) => onSelectAll?.(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
          key: 'checkbox',
          render: (row: MemberDTO) => (
            <input
              type="checkbox"
              checked={selectedIds.has(row.id ?? '')}
              onChange={(e) => onSelectRow?.(row.id ?? '', e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
        },
        {
          label: 'Member',
          key: 'member',
          sortable: true,
          sortKey: 'FirstName',
          render: (row: MemberDTO) => (
            <div className="flex flex-col gap-1">
              <span>
                {row.firstName} {row.lastName}
              </span>
              <span className="break-all">{row.idPassportNumber ?? ''}</span>
              <MemberNoteIcons member={row} tableContext="members" recordId={row.id} />
            </div>
          ),
        },
        {
          label: 'Cellphone',
          key: 'phone',
          render: (row: MemberDTO) => (
            <span className="break-all">{row.phone ?? ''}</span>
          ),
        },
        {
          label: 'Email',
          key: 'email',
          sortable: true,
          sortKey: 'Email',
          className: 'hidden 2xl:table-cell',
          render: (row: MemberDTO) => (
            <span className="break-all">{row.email ?? ''}</span>
          ),
        },

        ...(statusFromUrl === 'newregistration'
          ? [
            {
              label: 'Type',
              key: 'memberRegistrationTypeId',
              sortable: true,
              sortKey: 'MemberRegistrationTypeTitle',
              render: (row: MemberDTO) => (
                <span>{row.memberRegistrationType?.title}</span>
              ),
            },
            {
              label: 'Step',
              key: 'memberRegistrationStepId',
              sortable: true,
              sortKey: 'MemberRegistrationStepTitle',
              render: (row: MemberDTO) => {
                const base =
                  row.memberRegistrationStep?.colourGrading?.colour ??
                  '#ffffffff';
                const { bg, text } = getBadgeColors(base, 0.18, 0.4);
                return base ? (
                  <Badge
                    label={row.memberRegistrationStep?.title || 'Unknown'}
                    variant="custom"
                    customBgColor={bg}
                    customTextColor={text}
                  />
                ) : (
                  <span>
                    {row.memberRegistrationStep?.title || 'Unknown'}
                  </span>
                );
              },
            },
          ]
          : []),
        {
          label: 'Membership',
          key: 'memberTypeId',
          sortable: true,
          sortKey: 'MemberTypeTitle',
          render: (row: MemberDTO) => <span>{row.memberType?.title}</span>,
        },
        {
          label: 'FCA',
          key: 'memberCategoryId',
          sortable: true,
          sortKey: 'MemberCategoryTitle',
          render: (row: MemberDTO) => <span>{row.memberCategory?.titleShort}</span>,
        },
        ...(statusFromUrl === 'newregistration'
          ? [
            {
              label: 'Payment',
              key: 'paymentTypeId',
              sortable: true,
              sortKey: 'PaymentTypeTitle',
              render: (row: MemberDTO) => {
                const base =
                  row.paymentType?.colourGrading?.colour ?? '#ffffffff';
                const { bg, text } = getBadgeColors(base, 0.18, 0.4);
                return base ? (
                  <Badge
                    label={row.paymentType?.title || 'Unknown'}
                    variant="custom"
                    customBgColor={bg}
                    customTextColor={text}
                  />
                ) : (
                  <span>{row.paymentType?.title || 'Unknown'}</span>
                );
              },
            },
            {
              label: 'Proof',
              key: 'proofOfPaymentFileId',
              render: (row: MemberDTO) => (
                <FileIcons
                  files={[
                    {
                      id: row.proofOfPaymentFileId,
                      label: 'Proof',
                    },
                  ]}
                />
              ),
            },
            {
              label: 'Confirmed',
              key: 'dateMemberConfirmed',
              render: (row: MemberDTO) =>
                renderBooleanBadge(
                  row.dateMemberConfirmed ? true : false,
                  'Yes',
                  'No',
                ),
            },
            {
              label: 'Invoiced',
              key: 'dateInvoiceCreated',
              render: (row: MemberDTO) =>
                renderBooleanBadge(
                  row.dateInvoiceCreated ? true : false,
                  'Yes',
                  'No',
                ),
            },
          ]
          : []),

        ...(statusFromUrl != 'newregistration'
          ? [
            {
              label: 'Enrolled',
              key: 'dateEnrolled',
              sortable: true,
              sortKey: 'DateEnrolled',
              format: (value: Date) =>
                convertToUserTime(value?.toString() ?? '', true),
            },
            {
              label: 'Renewal',
              key: 'paidUpTo',
              sortable: true,
              sortKey: 'PaidUpTo',
              format: (value: Date) =>
                convertToUserTime(value?.toString() ?? '', true),
            },
            {
              label: 'Disciplinary',
              key: 'memberDisciplinaryStatusId',
              sortable: true,
              sortKey: 'MemberDisciplinaryStatusTitle',
              render: (row: MemberDTO) => {
                const base =
                  row.memberDisciplinaryStatus?.colourGrading?.colour ??
                  '#ffffffff';
                const { bg, text } = getBadgeColors(base, 0.18, 0.4);
                return base ? (
                  <Badge
                    label={row.memberDisciplinaryStatus?.title || 'Unknown'}
                    variant="custom"
                    customBgColor={bg}
                    customTextColor={text}
                  />
                ) : (
                  <span>
                    {row.memberDisciplinaryStatus?.title || 'Unknown'}
                  </span>
                );
              },
            },
            {
              label: 'Termination',
              key: 'memberTerminationStatusId',
              sortable: true,
              sortKey: 'MemberTerminationStatusTitle',
              render: (row: MemberDTO) => {
                const base =
                  row.memberTerminationStatus?.colourGrading?.colour ??
                  '#ffffffff';
                const { bg, text } = getBadgeColors(base, 0.18, 0.4);
                return base ? (
                  <Badge
                    label={row.memberTerminationStatus?.title || 'Unknown'}
                    variant="custom"
                    customBgColor={bg}
                    customTextColor={text}
                  />
                ) : (
                  <span>
                    {row.memberTerminationStatus?.title || 'Unknown'}
                  </span>
                );
              },
            },
            {
              label: 'Archived',
              key: 'archivedUtc',
              className: 'hidden 2xl:table-cell',
              format: (value: Date) =>
                convertToUserTime(value?.toString() ?? '', true),
            },
            {
              label: 'Deleted',
              key: 'deletedUtc',
              className: 'hidden 2xl:table-cell',
              format: (value: Date) =>
                convertToUserTime(value?.toString() ?? '', true),
            },
          ]
          : []),
        {
          label: 'Status',
          key: 'memberStatusId',
          sortable: true,
          sortKey: 'MemberStatusTitle',
          render: (row: MemberDTO) => {
            // Check for deleted first, then archived
            if (row.isDeleted) {
              return <Badge label="Deleted" variant="error" />;
            }

            if (row.isArchived) {
              return <Badge label="Archived" variant="error" />;
            }

            // If not deleted or archived, show regular member status
            const base = row.memberStatus?.colourGrading?.colour ?? '#ffffffff';
            const { bg, text } = getBadgeColors(base, 0.18, 0.4);
            return base ? (
              <Badge
                label={row.memberStatus?.title || 'Unknown'}
                variant="custom"
                customBgColor={bg}
                customTextColor={text}
              />
            ) : (
              <span>{row.memberStatus?.title || 'Unknown'}</span>
            );
          },
        },
        {
          label: 'Updated',
          key: 'updatedUtc',
          sortable: true,
          sortKey: 'UpdatedUtc',
          className: 'hidden 2xl:table-cell',
          format: (value: Date) =>
            convertToUserTime(value?.toString() ?? ''),
        },
        {
          label: 'Added',
          key: 'createdUtc',
          sortable: true,
          sortKey: 'CreatedUtc',
          format: (value: Date) =>
            convertToUserTime(value?.toString() ?? ''),
        },
      ]}
      data={data}
      actions={{
        onEdit: handleEdit,
        extra: (row) =>
          createMemberActions({
            onViewMemberDetails: handleViewMemberDetails,
            onView: handleEdit,
            onDelete: handleDelete,
            onSendEmail: handleSendEmail,
            onViewRecordEmails: handleViewRecordEmails,
            onArchive: handleArchive,
            onRestore: handleRestore,
            onRestoreFromArchive: handleRestoreFromArchive,
            onImpersonateMemberLogin: handleImpersonateMemberLogin,
            onChangeStatus: handleChangeStatus,
            onAddNote: handleAddNote,
            onViewNoteHistory: handleViewNoteHistory,
            onViewInvoiceHistory: handleViewInvoiceHistory,
            onSendProofOfPaymentLink: handleSendProofOfPaymentLink,
            onSendPasswordResetLink: handleSendPasswordResetLink,
            onCompleteEftPayment: handleCompleteEftPayment,
            onViewMandate: handleViewMandate,
            onEditMandate: handleEditMandate,
          })(row),
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      sortBy={sortBy}
      sortDescending={sortDescending}
      onSort={onSort}
    />
  );
};

export default MemberTable;
